import type { IconName } from '@metrodev/core-web';
import { UserRole } from '@mirasoles/shared';

/**
 * Grupo funcional al que pertenece cada item del menu. El sidebar
 * (`Layout.tsx`) renderiza los items seccionados por grupo, en el orden
 * de `GROUP_ORDER`. El grupo `general` no lleva encabezado.
 */
export type NavGroup =
  | 'general'
  | 'cocina'
  | 'compras'
  | 'logistica'
  | 'stock'
  | 'administracion'
  | 'configuracion';

export interface NavItem {
  label: string;
  path: string;
  icon: IconName;
  /** Obligatorio: seccion del sidebar. El orden de `GROUP_ORDER` define el orden en la UI. */
  group: NavGroup;
  /** Rol jerarquico minimo requerido (herencia: Gerencia ve todo lo que ve el resto). */
  minRole?: UserRole;
  /** Roles EXACTOS permitidos (sin herencia por nivel). Si está definido, tiene prioridad sobre `minRole`. */
  exactRoles?: UserRole[];
}

/** Orden fijo de las secciones en el sidebar. `general` va primero y sin encabezado. */
export const GROUP_ORDER: NavGroup[] = [
  'general',
  'cocina',
  'compras',
  'logistica',
  'stock',
  'administracion',
  'configuracion',
];

/** Texto del encabezado de cada seccion (el grupo `general` no tiene). */
export const GROUP_LABELS: Record<Exclude<NavGroup, 'general'>, string> = {
  cocina: 'Operación de cocina',
  compras: 'Compras',
  logistica: 'Logística y depósito',
  stock: 'Stock',
  administracion: 'Administración y finanzas',
  configuracion: 'Configuración',
};

// Cada entrada usa un icono distinto (ISSUE-88): no se repite ninguna clave de
// icono entre dos opciones del menú, y cada una guarda relación semántica con su
// función. El test de unicidad vive en navigation.test.ts.
export const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: 'dashboard', group: 'general' },
  { label: 'Notificaciones', path: '/notifications', icon: 'bell', group: 'general' },

  { label: 'Platos', path: '/dishes', icon: 'dish', group: 'cocina' },
  { label: 'Planificación', path: '/planning-periods', icon: 'monitor', group: 'cocina' },
  { label: 'Apertura de servicio', path: '/service-openings', icon: 'calendar', group: 'cocina' },
  { label: 'Cierre de servicio', path: '/service-consumptions', icon: 'check-circle', group: 'cocina' },
  { label: 'Control de la supervisora', path: '/supervisor-control', icon: 'eye', group: 'cocina', minRole: UserRole.SUPERVISION },
  { label: 'Proyección de demanda', path: '/demand-projections', icon: 'trending-up', group: 'cocina', minRole: UserRole.ADMINISTRACION },
  { label: 'Conciliación de consumos', path: '/reconciliation-periods', icon: 'git-branch', group: 'cocina', minRole: UserRole.SUPERVISION },
  { label: 'Satisfacción del comensal', path: '/diner-satisfaction', icon: 'alert-circle', group: 'cocina', minRole: UserRole.SUPERVISION },
  { label: 'Checklist BPM', path: '/bpm-checklist', icon: 'clipboard', group: 'cocina', minRole: UserRole.SUPERVISION },
  { label: 'Hallazgos', path: '/bpm-findings', icon: 'alert-triangle', group: 'cocina' },
  { label: 'Pedido de cocina', path: '/kitchen-orders/review', icon: 'shopping-bag', group: 'cocina', minRole: UserRole.COCINA },
  { label: 'Mis pedidos', path: '/kitchen-orders/mine', icon: 'list', group: 'cocina', minRole: UserRole.COCINA },
  { label: 'Pedido de supervisor', path: '/supervisor-orders/new', icon: 'user', group: 'cocina', minRole: UserRole.SUPERVISION },
  { label: 'Recepción en Cocina', path: '/kitchen-receptions', icon: 'log-in', group: 'cocina', minRole: UserRole.COCINA },

  { label: 'Proveedores', path: '/suppliers', icon: 'truck', group: 'compras', minRole: UserRole.COMPRAS_LOGISTICA },
  { label: 'Precios', path: '/supplier-prices/comparator', icon: 'tag', group: 'compras', minRole: UserRole.COMPRAS_LOGISTICA },
  { label: 'Cambios de precio', path: '/supplier-price-proposals', icon: 'external-link', group: 'compras', minRole: UserRole.COMPRAS_LOGISTICA },
  { label: 'Documentación de proveedores', path: '/supplier-documents', icon: 'user-plus', group: 'compras', minRole: UserRole.COMPRAS_LOGISTICA },
  { label: 'Acuerdos comerciales', path: '/commercial-agreements', icon: 'award', group: 'compras', minRole: UserRole.ADMINISTRACION },
  { label: 'Bandeja de pedidos', path: '/purchasing/inbox', icon: 'inbox', group: 'compras', minRole: UserRole.COMPRAS_LOGISTICA },
  { label: 'Consolidación de compras', path: '/purchase-consolidations', icon: 'git-merge', group: 'compras', minRole: UserRole.COMPRAS_LOGISTICA },
  { label: 'Órdenes de Compra', path: '/purchase-orders', icon: 'shopping-cart', group: 'compras', minRole: UserRole.COMPRAS_LOGISTICA },
  { label: 'Facturas de compra', path: '/purchase-invoices', icon: 'file-text', group: 'compras', minRole: UserRole.ADMINISTRACION },

  { label: 'Depósitos', path: '/warehouses', icon: 'archive', group: 'logistica', minRole: UserRole.ADMINISTRACION },
  { label: 'Sites', path: '/sites', icon: 'map-pin', group: 'logistica' },
  { label: 'Recepción Depósito', path: '/warehouse-receptions', icon: 'download', group: 'logistica', minRole: UserRole.OPERADOR_LOGISTICO },
  { label: 'Hoja de Ruta', path: '/picking-routes/builder', icon: 'map', group: 'logistica', minRole: UserRole.OPERADOR_LOGISTICO },
  { label: 'Hoja de Reparto', path: '/picking-routes/delivery', icon: 'truck', group: 'logistica', minRole: UserRole.OPERADOR_LOGISTICO },

  { label: 'Stock por ubicación', path: '/stock/balances', icon: 'package', group: 'stock' },
  { label: 'Conteo de stock', path: '/stock/counts', icon: 'list', group: 'stock' },
  { label: 'Mermas', path: '/stock/waste', icon: 'trending-down', group: 'stock' },
  { label: 'Ajuste manual de stock', path: '/stock/manual-adjustments', icon: 'edit-2', group: 'stock', minRole: UserRole.GERENCIA },
  { label: 'Fraccionamiento de lote', path: '/stock/lots/fractioning', icon: 'grid', group: 'stock', minRole: UserRole.GERENCIA },

  { label: 'Usuarios', path: '/users', icon: 'users', group: 'administracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Clientes', path: '/clients', icon: 'briefcase', group: 'administracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Reclamos de clientes', path: '/client-claims', icon: 'message-circle', group: 'administracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Cuenta Corriente de Proveedores', path: '/account-ledger/suppliers', icon: 'repeat', group: 'administracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Cuenta Corriente de Clientes', path: '/account-ledger/clients', icon: 'dollar-sign', group: 'administracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Propuesta de Pagos', path: '/payment-proposals', icon: 'send', group: 'administracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Reglas de Retención', path: '/withholding-rules', icon: 'percent', group: 'administracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Cuentas Bancarias/Caja', path: '/bank-accounts', icon: 'credit-card', group: 'administracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Cash flow', path: '/cash-flow', icon: 'clock', group: 'administracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Preliquidación de Facturación', path: '/preliquidaciones', icon: 'pie-chart', group: 'administracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Reportes', path: '/reports', icon: 'home', group: 'administracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Estado de Resultado por Cliente', path: '/estado-resultado-cliente', icon: 'bar-chart-2', group: 'administracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Presupuestos de comedor', path: '/presupuestos-comedor', icon: 'check-square', group: 'administracion', exactRoles: [UserRole.ADMINISTRACION, UserRole.GERENCIA] },
  { label: 'Caja Chica', path: '/petty-cash', icon: 'book', group: 'administracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Conciliación de sobres', path: '/petty-cash/reconciliation', icon: 'external-link', group: 'administracion', minRole: UserRole.ADMINISTRACION },

  { label: 'Artículos', path: '/articles', icon: 'box', group: 'configuracion' },
  { label: 'Categorías', path: '/categories', icon: 'filter', group: 'configuracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Unidades de medida', path: '/units-of-measure', icon: 'info', group: 'configuracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Condiciones de pago', path: '/payment-terms', icon: 'check', group: 'configuracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Valores nutricionales', path: '/nutritional-values', icon: 'book-open', group: 'configuracion', minRole: UserRole.ADMINISTRACION },
  { label: 'Parámetros de negocio', path: '/business-parameters', icon: 'settings', group: 'configuracion', minRole: UserRole.GERENCIA },
  { label: 'Plantillas', path: '/document-templates', icon: 'layers', group: 'configuracion', minRole: UserRole.GERENCIA },
  { label: 'Auditoría', path: '/audit-logs', icon: 'shield', group: 'configuracion', minRole: UserRole.GERENCIA },
  { label: 'Checklist BPM maestro', path: '/bpm-checklist/master', icon: 'menu', group: 'configuracion', exactRoles: [UserRole.ADMINISTRACION, UserRole.GERENCIA] },
  { label: 'Categorías de caja chica', path: '/petty-cash/categories', icon: 'search', group: 'configuracion', minRole: UserRole.ADMINISTRACION },
];
